const ORDER_KEY = "pickle.deck.order";
const CURSOR_KEY = "pickle.deck.cursor";

const shuffle = <T,>(arr: readonly T[]): T[] => {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

const readOrder = (): string[] | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(ORDER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.every((x) => typeof x === "string")
      ? parsed
      : null;
  } catch {
    return null;
  }
};

const writeOrder = (order: readonly string[]): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(ORDER_KEY, JSON.stringify(order));
  } catch {
    /* ignore */
  }
};

/**
 * Returns a persisted shuffled order of every approved attendee except `me`.
 * If the persisted order is missing some currently-known ids (e.g. the guest
 * list grew since last visit), those ids are appended to the end rather than
 * triggering a full reshuffle — progress stays intact.
 */
export const loadOrBuildDeck = (
  meGuestId: string,
  allIds: readonly string[],
): string[] => {
  const candidatesSet = new Set(allIds.filter((id) => id !== meGuestId));
  const saved = readOrder();

  if (saved) {
    const filtered = saved.filter((id) => candidatesSet.has(id));
    const seen = new Set(filtered);
    const newcomers = [...candidatesSet].filter((id) => !seen.has(id));
    const next = [...filtered, ...newcomers];
    if (
      next.length !== saved.length ||
      next.some((id, i) => id !== saved[i])
    ) {
      writeOrder(next);
    }
    return next;
  }

  const fresh = shuffle([...candidatesSet]);
  writeOrder(fresh);
  return fresh;
};

export const getCursor = (): number => {
  if (typeof window === "undefined") return 0;
  try {
    const raw = window.localStorage.getItem(CURSOR_KEY);
    const n = raw ? Number.parseInt(raw, 10) : 0;
    return Number.isFinite(n) && n >= 0 ? n : 0;
  } catch {
    return 0;
  }
};

export const setCursor = (n: number): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CURSOR_KEY, String(Math.max(0, n)));
  } catch {
    /* ignore */
  }
};

export const resetDeck = (): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(ORDER_KEY);
    window.localStorage.removeItem(CURSOR_KEY);
  } catch {
    /* ignore */
  }
};

export const ROUND_SIZE = 5;
